import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, of, switchMap } from 'rxjs';
import { Httpservice } from '../../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import {
  buildPaginationItems,
  buildPaginationLabel,
  buildQueryString,
  buildTimestampFileName,
  canShowPagination,
  downloadBlob,
  extractPagination,
  getApiErrorMessage,
} from '../../../helper/table-utils';
import { Commonservice } from '../../../services/commonservice';
import { environment } from '../../../environments/environment';
import { CustomTooltipComponent } from '../../../helper/custom-helper/custom-tooltip';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import {
  createManageGeneratorFormValue,
  GENERATOR_FUEL_TYPE_OPTIONS,
  GENERATOR_OWNER_OPTIONS,
  GENERATOR_STATUS_OPTIONS,
  GENERATOR_TYPE_OPTIONS,
  LOG_SHEET_OPTIONS,
} from './manage-generator.data';
import { ManageGeneratorAutocompleteComponent } from './manage-generator-autocomplete';
import { MeterAddModalComponent } from '../manage-meter/meter-add-modal';
import {
  ManageGeneratorContactDetailFormValue,
  ManageGeneratorContactDetailItem,
  ManageGeneratorFormValue,
  ManageGeneratorItem,
  ManageGeneratorLookupOption,
  ManageGeneratorLocalLevelOption,
  ManageGeneratorPagination,
  ManageGeneratorPayload,
} from './manage-generator.interfaces';

type ManageGeneratorModalMode = 'create' | 'edit' | 'view';
type ManageGeneratorSortField = 'generator_name' | 'installed_capacity' | 'scada_code' | 'province';
type ManageGeneratorSortOrder = 'asc' | 'desc';
const EMAIL_PATTERN = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/;
const CONTACT_PERSON_PATTERN = /^[A-Za-z\s]*$/;
const MOBILE_NUMBER_PATTERN = /^\d{10}$/;
type ManageGeneratorQueryValue = string | number | null | undefined;
type ManageGeneratorQueryParams = Record<string, ManageGeneratorQueryValue>;
type ManageGeneratorFormFieldValue = string | number | null | undefined;
type ManageGeneratorAutocompleteEvent = Event | { value?: string; query?: string; originalEvent?: Event | null };
interface GeneratorMeter { id: number; serial_no: number; meter_no: string; mfr_name: string; meter_type: string; mf_value: number | null; }

const GENERATOR_ALL_FILTER_VALUE = 'all';
const GENERATOR_NO_RECORDS_LABEL = 'No records';
const GENERATOR_SHOWING_RESULTS_LABEL = 'Showing';
const GENERATOR_RESULTS_LABEL = 'results';
const GENERATOR_ONLY_XLSX_MESSAGE = 'Only .xlsx files are allowed.';
const GENERATOR_REQUIRED_FIELDS_MESSAGE = 'Please fill in the required fields before saving.';
const GENERATOR_FAILED_LOAD_SUBSTATIONS_MESSAGE = 'Failed to load generator substations:';
const GENERATOR_FAILED_LOAD_VOLTAGE_LEVELS_MESSAGE = 'Failed to load voltage levels:';
const GENERATOR_FAILED_LOAD_PROVINCES_MESSAGE = 'Failed to load provinces:';
const GENERATOR_FAILED_LOAD_DISTRICTS_MESSAGE = 'Failed to load districts:';
const GENERATOR_FAILED_LOAD_LOCAL_LEVELS_MESSAGE = 'Failed to load local levels:';
const GENERATOR_FAILED_LOAD_GENERATORS_MESSAGE = 'Failed to load generators:';
const GENERATOR_FAILED_EXPORT_MESSAGE = 'Failed to export generators.';
const GENERATOR_FAILED_TEMPLATE_MESSAGE = 'Failed to download template.';
const GENERATOR_FAILED_ATTACHMENT_MESSAGE = 'Generator saved, but the file could not be attached.';
const GENERATOR_FAILED_SAVE_MESSAGE = 'Failed to save generator.';
const GENERATOR_FAILED_UPDATE_STATUS_MESSAGE = 'Failed to update generator status:';
const GENERATOR_FILE_NAME_DISPLAY_MAX_LENGTH = 30;
const GENERATOR_OTHER_PARAMETER_FILTER_OPTIONS = [
  { label: 'Water Level', value: 'Water Level' },
  { label: 'Kathmandu Load', value: 'KathmanduLoad' },
] as const;

@Component({
  selector: 'app-manage-generator-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DashboardSidebarComponent,
    DashboardHeaderComponent,
    CustomTooltipComponent,
    ManageGeneratorAutocompleteComponent,
    MeterAddModalComponent,
  ],
  templateUrl: './manage-generator.html',
  styleUrl: './manage-generator.scss',
})
export class ManageGeneratorPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly allFilterValue = GENERATOR_ALL_FILTER_VALUE;
  protected readonly generatorTypeOptions = GENERATOR_TYPE_OPTIONS;
  protected readonly fuelTypeOptions = GENERATOR_FUEL_TYPE_OPTIONS;
  protected readonly ownerOptions = GENERATOR_OWNER_OPTIONS;
  protected readonly logSheetOptions = LOG_SHEET_OPTIONS;
  protected readonly otherParameterOptions = ['Water Level', 'KathmanduLoad'];
  protected readonly otherParameterFilterOptions = GENERATOR_OTHER_PARAMETER_FILTER_OPTIONS;
  protected readonly statusOptions = GENERATOR_STATUS_OPTIONS;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl: FormControl;
  protected readonly availableMeterSearchControl = new FormControl('');
  protected readonly availableMeterPageSizeControl = new FormControl(15);

  protected readonly filterForm: UntypedFormGroup;
  protected readonly generatorForm: UntypedFormGroup;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isDownloadingTemplate = false;
  protected isImporting = false;
  protected isGeneratorActionMenuOpen = false;
  protected contactModalOpen = false;
  protected generatorModalOpen = false;
  protected deleteModalOpen = false;
  protected modalMode: ManageGeneratorModalMode = 'create';
  protected selectedGenerator: ManageGeneratorItem | null = null;
  protected selectedGeneratorContacts: ManageGeneratorItem | null = null;
  protected selectedGeneratorForMeters: ManageGeneratorItem | null = null;
  protected generatorMetersModalOpen = false;
  protected generatorMeterPickerModalOpen = false;
  protected isGeneratorMetersLoading = false;
  protected isAvailableMetersLoading = false;
  protected isMeterAssignmentSaving = false;
  protected generatorMeters: GeneratorMeter[] = [];
  protected availableMeters: GeneratorMeter[] = [];
  protected selectedAvailableMeterIds = new Set<number>();
  protected addMeterModalOpen = false;
  protected formSubmitted = false;
  protected sortBy: ManageGeneratorSortField | null = null;
  protected sortOrder: ManageGeneratorSortOrder = 'asc';
  protected isCreatingContactCredentials = false;

  protected generators: ManageGeneratorItem[] = [];
  protected filteredGenerators: ManageGeneratorItem[] = [];
  protected pagination: ManageGeneratorPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };
  protected availableMeterPagination: ManageGeneratorPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  protected substationOptions: ManageGeneratorLookupOption[] = [];
  protected filteredSubstationOptions: ManageGeneratorLookupOption[] = [];
  protected filteredFilterSubstationOptions: ManageGeneratorLookupOption[] = [];
  protected entityOptions: ManageGeneratorLookupOption[] = [];
  protected filteredEntityOptions: ManageGeneratorLookupOption[] = [];
  protected voltageLevelOptions: ManageGeneratorLookupOption[] = [];
  protected provinceOptions: ManageGeneratorLookupOption[] = [];
  protected filteredProvinceOptions: ManageGeneratorLookupOption[] = [];
  protected filterProvinceOptions: ManageGeneratorLookupOption[] = [];
  protected filteredFilterProvinceOptions: ManageGeneratorLookupOption[] = [];
  protected districtOptions: ManageGeneratorLookupOption[] = [];
  protected filteredDistrictOptions: ManageGeneratorLookupOption[] = [];
  protected filterDistrictOptions: ManageGeneratorLookupOption[] = [];
  protected filteredFilterDistrictOptions: ManageGeneratorLookupOption[] = [];
  protected localLevelOptions: ManageGeneratorLocalLevelOption[] = [];
  protected localLevelSearchOptions: ManageGeneratorLookupOption[] = [];
  protected filteredLocalLevelOptions: ManageGeneratorLookupOption[] = [];
  protected filterLocalLevelOptions: ManageGeneratorLookupOption[] = [];
  protected filteredFilterLocalLevelOptions: ManageGeneratorLookupOption[] = [];
  protected entitySearchTerm = '';
  protected substationSearchTerm = '';
  protected filterSubstationSearchTerm = '';
  protected filterProvinceSearchTerm = '';
  protected filterDistrictSearchTerm = '';
  protected filterLocalLevelSearchTerm = '';
  protected provinceSearchTerm = '';
  protected districtSearchTerm = '';
  protected localLevelSearchTerm = '';
  protected otherParameterDropdownOpen = false;
  protected otherParameterFilterDropdownOpen = false;
  protected selectedFile: File | null = null;
  protected selectedFileName = '';
  protected selectedFilePreviewUrl: string | null = null;
  protected existingFilePath: string | null = null;
  protected filePathCleared = false;
  protected submitErrorMessage = '';

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private entityOptionsLoaded = false;
  private readonly entityOptionsLoadWaiters: Array<() => void> = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
      substation_id: [GENERATOR_ALL_FILTER_VALUE],
      province_id: [GENERATOR_ALL_FILTER_VALUE],
      owner: [GENERATOR_ALL_FILTER_VALUE],
      generator_type: [GENERATOR_ALL_FILTER_VALUE],
      fuel_type: [GENERATOR_ALL_FILTER_VALUE],
      other_parameter: [[]],
      district_id: [GENERATOR_ALL_FILTER_VALUE],
      local_level_id: [GENERATOR_ALL_FILTER_VALUE],
      page_size: [15],
    });
    this.pageSizeControl = this.filterForm.get('page_size') as FormControl;

    this.generatorForm = this.fb.group({
      generator_type: ['', [Validators.required]],
      fuel_type: [''],
      generator_name: ['', [Validators.required, Validators.maxLength(255)]],
      promoter_company: ['', [Validators.maxLength(255)]],
      project_unique_code: ['', [Validators.maxLength(100)]],
      owner: ['NEA', [Validators.maxLength(100)]],
      delivery_point: [''],
      corridor: [''],
      ppa_date: [''],
      substation_id: [null],
      scada_station_code: ['', [Validators.maxLength(50)]],
      entity_id: [null],
      connecting_voltage_level_id: [null],
      voltage_level_id: [null],
      province_id: [null, Validators.required],
      district_id: [null, Validators.required],
      local_level_id: [null],
      local_level: ['', [Validators.maxLength(255)]],
      installed_capacity: ['', [Validators.maxLength(50)]],
      capacity: ['', [Validators.maxLength(50)]],
      no_of_units: ['', [Validators.maxLength(10)]],
      other_parameter: [[]],
      log_sheet: ['', [Validators.required]],
      act_as_substation: [false],
      latitude: ['', [Validators.maxLength(100)]],
      longitude: ['', [Validators.maxLength(100)]],
      status: ['ACTIVE', Validators.required],
      contact_details: this.fb.array([]),
    });
    this.resetContactDetailRows();
  }

  ngOnInit(): void {
    this.loadLookupData();
    this.loadGenerators(1);

    this.availableMeterSearchControl.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadAvailableMeters(1));

    this.availableMeterPageSizeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadAvailableMeters(1));

    this.filterForm.valueChanges
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadGenerators(1));

    this.generatorForm.get('province_id')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((provinceId) => this.onProvinceSelectionChange(provinceId));

    this.generatorForm.get('district_id')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((districtId) => this.onDistrictSelectionChange(districtId));

    this.generatorForm.get('generator_type')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((generatorType) => this.applyFuelTypeVisibility(generatorType));

    this.filterForm.get('generator_type')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((generatorType) => this.onFilterGeneratorTypeChange(generatorType));
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset(
      {
        search: '',
        substation_id: GENERATOR_ALL_FILTER_VALUE,
        province_id: GENERATOR_ALL_FILTER_VALUE,
        owner: GENERATOR_ALL_FILTER_VALUE,
        generator_type: GENERATOR_ALL_FILTER_VALUE,
        fuel_type: GENERATOR_ALL_FILTER_VALUE,
        other_parameter: [],
        district_id: GENERATOR_ALL_FILTER_VALUE,
        local_level_id: GENERATOR_ALL_FILTER_VALUE,
        page_size: this.pageSizeControl.value ?? 15,
      },
      { emitEvent: false },
    );
    this.isGeneratorActionMenuOpen = false;
    this.otherParameterFilterDropdownOpen = false;
    this.entitySearchTerm = '';
    this.substationSearchTerm = '';
    this.filterSubstationSearchTerm = '';
    this.filterProvinceSearchTerm = '';
    this.filterDistrictSearchTerm = '';
    this.filterLocalLevelSearchTerm = '';
    this.filteredEntityOptions = [...this.entityOptions];
    this.filteredSubstationOptions = [...this.substationOptions];
    this.filteredFilterSubstationOptions = [...this.substationOptions];
    this.filteredFilterProvinceOptions = [...this.filterProvinceOptions];
    this.filterDistrictOptions = [];
    this.filteredFilterDistrictOptions = [];
    this.filterLocalLevelOptions = [];
    this.filteredFilterLocalLevelOptions = [];
    this.loadGenerators(1);
  }

  protected openGeneratorMetersModal(generator: ManageGeneratorItem): void {
    this.selectedGeneratorForMeters = generator;
    this.generatorMetersModalOpen = true;
    this.generatorMeterPickerModalOpen = false;
    this.selectedAvailableMeterIds.clear();
    this.loadGeneratorMeters(generator.id);
  }

  protected closeGeneratorMetersModal(): void {
    this.generatorMetersModalOpen = false;
    this.generatorMeterPickerModalOpen = false;
    this.selectedGeneratorForMeters = null;
    this.generatorMeters = [];
    this.availableMeters = [];
    this.selectedAvailableMeterIds.clear();
  }

  protected openMeterPickerModal(): void {
    if (!this.selectedGeneratorForMeters) return;
    this.generatorMeterPickerModalOpen = true;
    this.selectedAvailableMeterIds.clear();
    this.availableMeterSearchControl.setValue('', { emitEvent: false });
    this.availableMeterPageSizeControl.setValue(15, { emitEvent: false });
    this.availableMeterPagination = { page_no: 1, page_size: 15, total_records: 0, total_pages: 0 };
    this.loadAvailableMeters(this.selectedGeneratorForMeters.id, 1);
  }

  protected closeMeterPickerModal(): void { this.generatorMeterPickerModalOpen = false; this.availableMeters = []; this.availableMeterSearchControl.setValue('', { emitEvent: false }); this.selectedAvailableMeterIds.clear(); }
  protected openAddMeterModal(): void { this.addMeterModalOpen = true; }
  protected closeAddMeterModal(): void { this.addMeterModalOpen = false; }
  protected onMeterCreated(): void { this.addMeterModalOpen = false; if (this.selectedGeneratorForMeters) this.loadAvailableMeters(this.selectedGeneratorForMeters.id); }
  protected isAvailableMeterSelected(meterId: number): boolean { return this.selectedAvailableMeterIds.has(meterId); }
  protected getAvailableMeterPaginationLabel(): string { return buildPaginationLabel(this.availableMeterPagination, GENERATOR_NO_RECORDS_LABEL, GENERATOR_SHOWING_RESULTS_LABEL, GENERATOR_RESULTS_LABEL); }
  protected getAvailableMeterPaginationItems(): number[] { return buildPaginationItems(this.availableMeterPagination.total_pages, this.availableMeterPagination.page_no); }
  protected canShowAvailableMeterPagination(): boolean { return canShowPagination(this.availableMeterPagination.total_records); }
  protected goToAvailableMeterPage(pageNo: number): void { if (pageNo >= 1 && pageNo <= this.availableMeterPagination.total_pages && pageNo !== this.availableMeterPagination.page_no) this.reloadAvailableMeters(pageNo); }
  protected goToPreviousAvailableMeterPage(): void { this.goToAvailableMeterPage(this.availableMeterPagination.page_no - 1); }
  protected goToNextAvailableMeterPage(): void { this.goToAvailableMeterPage(this.availableMeterPagination.page_no + 1); }
  protected selectAvailableMeter(meterId: number): void { this.selectedAvailableMeterIds.add(meterId); }
  protected deselectAvailableMeter(meterId: number): void { this.selectedAvailableMeterIds.delete(meterId); }
  protected updateAvailableMeterSelection(meterId: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.checked) {
      this.selectAvailableMeter(meterId);
      return;
    }
    this.deselectAvailableMeter(meterId);
  }
  protected saveSelectedMeters(): void {
    const generator = this.selectedGeneratorForMeters;
    if (!generator || this.isMeterAssignmentSaving || !this.selectedAvailableMeterIds.size) return;
    this.isMeterAssignmentSaving = true;
    this.httpSvc.requestCall(AuthEndPoints.ASSIGN_METERS_TO_GENERATOR, ApiMethod.POST, { generator_id: generator.id, meter_ids: Array.from(this.selectedAvailableMeterIds) })
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isMeterAssignmentSaving = false; this.cdr.markForCheck(); }))
      .subscribe({ next: () => { this.closeMeterPickerModal(); this.loadGeneratorMeters(generator.id); this.loadGenerators(this.pagination.page_no); }, error: error => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || 'Failed to assign meters.') });
  }
  protected unassignMeter(meter: GeneratorMeter): void {
    const generator = this.selectedGeneratorForMeters;
    if (!generator || this.isMeterAssignmentSaving) return;
    this.isMeterAssignmentSaving = true;
    this.httpSvc.requestCall(AuthEndPoints.UNASSIGN_METER_FROM_GENERATOR, ApiMethod.POST, { generator_id: generator.id, meter_id: meter.id })
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isMeterAssignmentSaving = false; this.cdr.markForCheck(); }))
      .subscribe({ next: () => { this.loadGeneratorMeters(generator.id); this.loadGenerators(this.pagination.page_no); } });
  }

  protected loadLookupData(): void {
    this.loadEntities();
    this.loadSubstations();
    this.loadVoltageLevels();
    this.loadProvinces();
  }

  protected loadEntities(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_ENTITIES_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.entityOptions = (res?.value?.entities ?? res?.entities ?? []).map((entity: any) => ({
            id: entity.id,
            name: entity.entity_name,
          }));
          this.filterEntityAutocomplete(this.entitySearchTerm);
          this.markEntityOptionsLoaded();
        },
        error: (err) => {
          console.error('Failed to load entities:', err);
          this.entityOptions = [];
          this.filteredEntityOptions = [];
          this.markEntityOptionsLoaded();
        },
      });
  }

  protected loadSubstations(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATOR_SUBSTATIONS_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.substationOptions = (res?.value?.substations ?? res?.substations ?? []).map((substation: any) => ({
            id: substation.id,
            name: substation.station_name,
          }));
          this.filterSubstationAutocomplete(this.substationSearchTerm);
          this.filterFilterSubstationAutocomplete(this.filterSubstationSearchTerm);
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_SUBSTATIONS_MESSAGE, err);
          this.substationOptions = [];
          this.filteredSubstationOptions = [];
          this.filteredFilterSubstationOptions = [];
        },
      });
  }

  protected loadVoltageLevels(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATOR_VOLTAGE_LEVELS_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.voltageLevelOptions = (res?.value?.voltage_levels ?? res?.voltage_levels ?? []).map((voltage: any) => ({
            id: voltage.id,
            name: `${voltage.level} ${voltage.unit}`,
          }));
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_VOLTAGE_LEVELS_MESSAGE, err);
          this.voltageLevelOptions = [];
        },
      });
  }

  protected loadProvinces(): void {
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATOR_STATES_LIST, ApiMethod.GET, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.provinceOptions = (res?.value?.states ?? res?.states ?? []).map((state: any) => ({
            id: state.id,
            name: state.state_name,
          }));
          this.filterProvinceOptions = [...this.provinceOptions];
          this.filterProvinceAutocomplete(this.provinceSearchTerm);
          this.filterFilterProvinceAutocomplete(this.filterProvinceSearchTerm);
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_PROVINCES_MESSAGE, err);
          this.provinceOptions = [];
          this.filteredProvinceOptions = [];
          this.filterProvinceOptions = [];
          this.filteredFilterProvinceOptions = [];
        },
      });
  }

  protected loadDistricts(provinceId: number | null, selectedDistrictId: number | null = null, syncGeneratorForm = true): void {
    if (provinceId === null || provinceId === undefined) {
      this.districtOptions = [];
      if (syncGeneratorForm && selectedDistrictId === null) {
        this.generatorForm.patchValue({ district_id: null }, { emitEvent: false });
      }
      return;
    }

    const query = this.buildQueryString({ state_id: provinceId });
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATOR_DISTRICTS_LIST, ApiMethod.GET, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.districtOptions = (res?.value?.districts ?? res?.districts ?? []).map((district: any) => ({
            id: district.id,
            name: district.district_name,
          }));
          this.filterDistrictAutocomplete(this.districtSearchTerm);

          if (syncGeneratorForm && selectedDistrictId !== null && selectedDistrictId !== undefined) {
            this.generatorForm.patchValue({ district_id: selectedDistrictId }, { emitEvent: false });
            this.syncDistrictAutocompleteValue(selectedDistrictId);
          }
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_DISTRICTS_MESSAGE, err);
          this.districtOptions = [];
          this.filteredDistrictOptions = [];
        },
      });
  }

  protected onProvinceSelectionChange(provinceId: number | null): void {
    if (provinceId === null || provinceId === undefined) {
      this.districtOptions = [];
      this.generatorForm.patchValue({ district_id: null }, { emitEvent: false });
      this.districtSearchTerm = '';
      this.clearLocalLevels();
      return;
    }

    this.generatorForm.patchValue({ district_id: null }, { emitEvent: false });
    this.districtSearchTerm = '';
    this.clearLocalLevels();
    this.loadDistricts(provinceId);
  }

  protected loadLocalLevels(districtId: number | null, selectedLocalLevelId: number | null = null, syncGeneratorForm = true): void {
    if (districtId === null || districtId === undefined) {
      this.clearLocalLevels();
      return;
    }

    const query = this.buildQueryString({ district_id: districtId });
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATOR_LOCAL_LEVELS_LIST, ApiMethod.GET, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.localLevelOptions = (res?.value?.local_levels ?? res?.local_levels ?? []).map((localLevel: any) => ({
            id: localLevel.id,
            local_name: localLevel.local_name,
            district_id: localLevel.district_id,
          }));
          this.localLevelSearchOptions = this.localLevelOptions.map((item) => ({
            id: item.id,
            name: item.local_name,
          }));
          this.filterLocalLevelAutocomplete(this.localLevelSearchTerm);

          if (syncGeneratorForm && selectedLocalLevelId !== null && selectedLocalLevelId !== undefined) {
            const selectedLocalLevelName =
              this.localLevelOptions.find((item) => item.id === selectedLocalLevelId)?.local_name ?? '';
            this.generatorForm.patchValue(
              {
                local_level_id: selectedLocalLevelId,
                local_level: selectedLocalLevelName,
              },
              { emitEvent: false },
            );
            this.syncLocalLevelAutocompleteValue(selectedLocalLevelId, selectedLocalLevelName);
          }
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_LOCAL_LEVELS_MESSAGE, err);
          this.clearLocalLevels();
        },
      });
  }

  protected loadFilterDistricts(provinceId: number | null, selectedDistrictId: number | null = null): void {
    if (provinceId === null || provinceId === undefined) {
      this.filterDistrictOptions = [];
      this.filteredFilterDistrictOptions = [];
      if (selectedDistrictId === null) {
        this.filterForm.patchValue({ district_id: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
      }
      return;
    }

    const query = this.buildQueryString({ state_id: provinceId });
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATOR_DISTRICTS_LIST, ApiMethod.GET, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.filterDistrictOptions = (res?.value?.districts ?? res?.districts ?? []).map((district: any) => ({
            id: district.id,
            name: district.district_name,
          }));
          this.filterDistrictAutocomplete(this.filterDistrictSearchTerm);
          this.filterFilterDistrictAutocomplete(this.filterDistrictSearchTerm);

          if (selectedDistrictId !== null && selectedDistrictId !== undefined) {
            this.filterForm.patchValue({ district_id: selectedDistrictId }, { emitEvent: false });
          }
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_DISTRICTS_MESSAGE, err);
          this.filterDistrictOptions = [];
          this.filteredFilterDistrictOptions = [];
        },
      });
  }

  protected loadFilterLocalLevels(districtId: number | null, selectedLocalLevelId: number | null = null): void {
    if (districtId === null || districtId === undefined) {
      this.filterLocalLevelOptions = [];
      this.filteredFilterLocalLevelOptions = [];
      this.filterForm.patchValue({ local_level_id: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
      return;
    }

    const query = this.buildQueryString({ district_id: districtId });
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATOR_LOCAL_LEVELS_LIST, ApiMethod.GET, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.filterLocalLevelOptions = (res?.value?.local_levels ?? res?.local_levels ?? []).map((localLevel: any) => ({
            id: localLevel.id,
            name: localLevel.local_name,
          }));
          this.filterLocalLevelAutocomplete(this.filterLocalLevelSearchTerm);
          this.filterFilterLocalLevelAutocomplete(this.filterLocalLevelSearchTerm);

          if (selectedLocalLevelId !== null && selectedLocalLevelId !== undefined) {
            this.filterForm.patchValue({ local_level_id: selectedLocalLevelId }, { emitEvent: false });
          }
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_LOCAL_LEVELS_MESSAGE, err);
          this.filterLocalLevelOptions = [];
          this.filteredFilterLocalLevelOptions = [];
        },
      });
  }

  protected onDistrictSelectionChange(districtId: number | null): void {
    this.generatorForm.patchValue(
      {
        local_level_id: null,
        local_level: '',
      },
      { emitEvent: false },
    );
    this.localLevelSearchTerm = '';
    this.loadLocalLevels(districtId);
  }

  protected get contactDetailsArray(): UntypedFormArray {
    return this.generatorForm.get('contact_details') as UntypedFormArray;
  }

  protected get contactDetailControls(): UntypedFormGroup[] {
    return this.contactDetailsArray.controls as UntypedFormGroup[];
  }

  protected addContactDetailRow(detail?: Partial<ManageGeneratorContactDetailFormValue>): void {
    if (this.modalMode === 'view') {
      return;
    }
    this.contactDetailsArray.push(this.createContactDetailGroup(detail));
  }

  protected removeContactDetailRow(index: number): void {
    if (this.modalMode === 'view') {
      return;
    }
    if (this.isContactDetailPortalUser(index)) {
      return;
    }
    if (this.contactDetailsArray.length <= 1) {
      this.contactDetailsArray.at(0).reset(
        {
          id: null,
          person_name: '',
          email: '',
          mobile_no: '',
          designation: '',
          is_portal_user: false,
        },
        { emitEvent: false },
      );
      return;
    }

    this.contactDetailsArray.removeAt(index);
  }

  protected isContactDetailFieldInvalid(index: number, fieldName: string): boolean {
    const group = this.contactDetailsArray.at(index);
    const control = group?.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected isContactDetailEmailInvalid(index: number): boolean {
    const group = this.contactDetailsArray.at(index);
    const control = group?.get('email');
    return Boolean(control?.hasError('pattern') && (control.touched || this.formSubmitted));
  }

  protected isContactDetailPersonInvalid(index: number): boolean {
    return this.isContactDetailPatternInvalid(index, 'person_name');
  }

  protected isContactDetailMobileInvalid(index: number): boolean {
    return this.isContactDetailPatternInvalid(index, 'mobile_no');
  }

  private loadGeneratorMeters(generatorId: number): void {
    this.isGeneratorMetersLoading = true;
    this.httpSvc.requestCall(AuthEndPoints.GET_GENERATOR_METERS, ApiMethod.GET, `?generator_id=${generatorId}`)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isGeneratorMetersLoading = false; this.cdr.markForCheck(); }))
      .subscribe({ next: res => { this.generatorMeters = res?.value?.meters ?? res?.meters ?? []; }, error: () => { this.generatorMeters = []; } });
  }

  private reloadAvailableMeters(pageNo = this.availableMeterPagination.page_no): void {
    if (this.generatorMeterPickerModalOpen && this.selectedGeneratorForMeters) this.loadAvailableMeters(this.selectedGeneratorForMeters.id, pageNo);
  }

  private loadAvailableMeters(generatorId: number, pageNo = this.availableMeterPagination.page_no): void {
    this.isAvailableMetersLoading = true;
    const query = buildQueryString({ generator_id: generatorId, search: this.availableMeterSearchControl.value?.trim() || undefined, page_no: pageNo, page_size: this.availableMeterPageSizeControl.value ?? 15 });
    this.httpSvc.requestCall(AuthEndPoints.GET_GENERATOR_AVAILABLE_METERS, ApiMethod.GET, query)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isAvailableMetersLoading = false; this.cdr.markForCheck(); }))
      .subscribe({ next: res => { const value = res?.value ?? res ?? {}; this.availableMeters = value.meters ?? []; this.availableMeterPagination = this.extractPagination(value) ?? { page_no: pageNo, page_size: this.availableMeterPageSizeControl.value ?? 15, total_records: 0, total_pages: 0 }; }, error: () => { this.availableMeters = []; this.availableMeterPagination = { page_no: pageNo, page_size: this.availableMeterPageSizeControl.value ?? 15, total_records: 0, total_pages: 0 }; } });
  }

  protected loadGenerators(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      substation_id: this.filterForm.get('substation_id')?.value,
      province_id: this.filterForm.get('province_id')?.value,
      owner: this.filterForm.get('owner')?.value,
      generator_type: this.filterForm.get('generator_type')?.value,
      fuel_type: this.filterForm.get('fuel_type')?.value,
      other_parameter: this.getSelectedOtherParameterFilterValue(),
      page_no: pageNo,
      page_size: this.filterForm.get('page_size')?.value,
      sort_by: this.sortBy,
      sort_order: this.sortBy ? this.sortOrder : null,
    });

    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATORS_LIST, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.generators = res?.value?.generators ?? res?.generators ?? [];
          this.filteredGenerators = [...this.generators];
          this.pagination = this.extractPagination(res?.value) ?? this.pagination;
        },
        error: (err) => {
          console.error(GENERATOR_FAILED_LOAD_GENERATORS_MESSAGE, err);
          this.generators = [];
          this.filteredGenerators = [];
          this.pagination = {
            page_no: pageNo,
            page_size: Number(this.filterForm.get('page_size')?.value ?? 10),
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }

  protected openAddGeneratorModal(): void {
    this.isGeneratorActionMenuOpen = false;
    this.contactModalOpen = false;
    this.selectedGeneratorContacts = null;
    this.modalMode = 'create';
    this.selectedGenerator = null;
    this.generatorForm.enable({ emitEvent: false });
    this.selectedFile = null;
    this.selectedFileName = '';
    this.selectedFilePreviewUrl = null;
    this.existingFilePath = null;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    this.entitySearchTerm = '';
    this.filteredEntityOptions = [];
    this.substationSearchTerm = '';
    this.filteredSubstationOptions = [];
    this.provinceSearchTerm = '';
    this.filteredProvinceOptions = [];
    this.districtSearchTerm = '';
    this.filteredDistrictOptions = [];
    this.localLevelSearchTerm = '';
    this.filteredLocalLevelOptions = [];
    this.localLevelSearchOptions = [];
    this.otherParameterDropdownOpen = false;
    this.generatorForm.reset(createManageGeneratorFormValue(), { emitEvent: false });
    this.resetContactDetailRows();
    this.districtOptions = [];
    this.clearLocalLevels();
    this.applyFuelTypeVisibility(null);
    this.formSubmitted = false;
    this.generatorModalOpen = true;
  }

  protected async openEditGeneratorModal(generator: ManageGeneratorItem): Promise<void> {
    this.isGeneratorActionMenuOpen = false;
    this.contactModalOpen = false;
    this.selectedGeneratorContacts = null;
    this.modalMode = 'edit';
    this.selectedGenerator = generator;
    this.generatorForm.enable({ emitEvent: false });
    this.selectedFile = null;
    this.selectedFileName = generator.file_path ? this.getFileName(generator.file_path) : '';
    this.selectedFilePreviewUrl = null;
    this.existingFilePath = generator.file_path;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    const contactRows = this.normalizeContactDetails(generator);
    const otherParameterSelections = this.splitCommaSeparatedValues(generator.other_parameter);
    const sourceType = this.normalizeSourceType(generator.generator_type);
    const fuelType = this.normalizeTextValue(generator.fuel_type);
    this.generatorForm.reset(
      {
        generator_type: sourceType,
        fuel_type: '',
        generator_name: generator.generator_name,
        promoter_company: generator.entity_name ?? generator.promoter_company ?? '',
        project_unique_code: generator.project_unique_code ?? '',
        owner: generator.owner ?? 'NEA',
        delivery_point: generator.delivery_point ?? '',
        corridor: generator.corridor ?? '',
        ppa_date: generator.ppa_date ?? '',
        substation_id: generator.substation_id,
        scada_station_code: generator.scada_station_code ?? '',
        entity_id: generator.entity_id,
        connecting_voltage_level_id: generator.connecting_voltage_level_id,
        voltage_level_id: generator.voltage_level_id,
        province_id: generator.province_id,
        district_id: generator.district_id,
        local_level_id: generator.local_level_id,
        local_level: generator.local_level_name ?? generator.local_level ?? '',
        installed_capacity: this.toFormString(generator.installed_capacity),
        capacity: this.toFormString(generator.capacity),
        no_of_units: this.toFormString(generator.no_of_units),
        other_parameter: otherParameterSelections,
        log_sheet: this.toLogSheetOptionValue(generator.logsheet, generator.log_sheet),
        act_as_substation: Boolean(generator.act_as_substation),
        latitude: generator.latitude ?? '',
        longitude: generator.longitude ?? '',
        status: this.normalizeStatus(generator.status),
        contact_details: [],
      },
      { emitEvent: false },
    );
    this.syncSubstationAutocompleteValue(generator.substation_id, generator.substation_name ?? '');
    this.resetContactDetailRows(contactRows);
    this.provinceSearchTerm = generator.province_name ?? '';
    this.districtSearchTerm = generator.district_name ?? '';
    this.localLevelSearchTerm = generator.local_level_name ?? generator.local_level ?? '';
    this.filterProvinceAutocomplete(this.provinceSearchTerm);
    this.filteredDistrictOptions = [...this.districtOptions];
    this.filteredLocalLevelOptions = [...this.localLevelSearchOptions];
    this.loadDistricts(generator.province_id, generator.district_id);
    this.loadLocalLevels(generator.district_id, generator.local_level_id);
    this.applyFuelTypeVisibility(sourceType);
    await this.waitForEntityOptions();
    this.syncEntityAutocompleteValue(generator.entity_id, generator.entity_name ?? generator.promoter_company ?? '');
    await Promise.resolve();
    if (sourceType === 'Hydro') {
      this.generatorForm.patchValue(
        {
          fuel_type: this.normalizeFuelTypeOptionValue(fuelType),
        },
        { emitEvent: false },
      );
    }
    this.formSubmitted = false;
    this.generatorModalOpen = true;
  }

  protected async openViewGeneratorModal(generator: ManageGeneratorItem): Promise<void> {
    this.isGeneratorActionMenuOpen = false;
    this.contactModalOpen = false;
    this.selectedGeneratorContacts = null;
    this.modalMode = 'view';
    this.selectedGenerator = generator;
    this.generatorForm.enable({ emitEvent: false });
    this.otherParameterDropdownOpen = false;
    this.selectedFile = null;
    this.selectedFileName = generator.file_path ? this.getFileName(generator.file_path) : '';
    this.selectedFilePreviewUrl = null;
    this.existingFilePath = generator.file_path;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    const contactRows = this.normalizeContactDetails(generator);
    const otherParameterSelections = this.splitCommaSeparatedValues(generator.other_parameter);
    const sourceType = this.normalizeSourceType(generator.generator_type);
    const fuelType = this.normalizeTextValue(generator.fuel_type);
    this.generatorForm.reset(
      {
        generator_type: sourceType,
        fuel_type: '',
        generator_name: generator.generator_name,
        promoter_company: generator.entity_name ?? generator.promoter_company ?? '',
        project_unique_code: generator.project_unique_code ?? '',
        owner: generator.owner ?? 'NEA',
        delivery_point: generator.delivery_point ?? '',
        corridor: generator.corridor ?? '',
        ppa_date: generator.ppa_date ?? '',
        substation_id: generator.substation_id,
        scada_station_code: generator.scada_station_code ?? '',
        entity_id: generator.entity_id,
        connecting_voltage_level_id: generator.connecting_voltage_level_id,
        voltage_level_id: generator.voltage_level_id,
        province_id: generator.province_id,
        district_id: generator.district_id,
        local_level_id: generator.local_level_id,
        local_level: generator.local_level_name ?? generator.local_level ?? '',
        installed_capacity: this.toFormString(generator.installed_capacity),
        capacity: this.toFormString(generator.capacity),
        no_of_units: this.toFormString(generator.no_of_units),
        other_parameter: otherParameterSelections,
        log_sheet: this.toLogSheetOptionValue(generator.logsheet, generator.log_sheet),
        act_as_substation: Boolean(generator.act_as_substation),
        latitude: generator.latitude ?? '',
        longitude: generator.longitude ?? '',
        status: this.normalizeStatus(generator.status),
        contact_details: [],
      },
      { emitEvent: false },
    );
    this.syncSubstationAutocompleteValue(generator.substation_id, generator.substation_name ?? '');
    this.resetContactDetailRows(contactRows);
    this.provinceSearchTerm = generator.province_name ?? '';
    this.districtSearchTerm = generator.district_name ?? '';
    this.localLevelSearchTerm = generator.local_level_name ?? generator.local_level ?? '';
    this.filterProvinceAutocomplete(this.provinceSearchTerm);
    this.filteredDistrictOptions = [...this.districtOptions];
    this.filteredLocalLevelOptions = [...this.localLevelSearchOptions];
    this.loadDistricts(generator.province_id, generator.district_id);
    this.loadLocalLevels(generator.district_id, generator.local_level_id);
    this.applyFuelTypeVisibility(sourceType);
    await this.waitForEntityOptions();
    this.syncEntityAutocompleteValue(generator.entity_id, generator.entity_name ?? generator.promoter_company ?? '');
    await Promise.resolve();
    if (sourceType === 'Hydro') {
      this.generatorForm.patchValue(
        {
          fuel_type: this.normalizeFuelTypeOptionValue(fuelType),
        },
        { emitEvent: false },
      );
    }
    this.formSubmitted = false;
    this.generatorModalOpen = true;
    this.generatorForm.disable({ emitEvent: false });
  }

  protected openDeleteGeneratorModal(generator: ManageGeneratorItem): void {
    this.isGeneratorActionMenuOpen = false;
    this.contactModalOpen = false;
    this.selectedGeneratorContacts = null;
    this.selectedGenerator = generator;
    this.deleteModalOpen = true;
  }

  protected closeGeneratorModal(): void {
    this.isGeneratorActionMenuOpen = false;
    this.contactModalOpen = false;
    this.selectedGeneratorContacts = null;
    this.generatorModalOpen = false;
    this.selectedGenerator = null;
    this.modalMode = 'create';
    this.generatorForm.enable({ emitEvent: false });
    this.selectedFile = null;
    this.selectedFileName = '';
    this.selectedFilePreviewUrl = null;
    this.existingFilePath = null;
    this.filePathCleared = false;
    this.submitErrorMessage = '';
    this.entitySearchTerm = '';
    this.filteredEntityOptions = [];
    this.substationSearchTerm = '';
    this.filteredSubstationOptions = [];
    this.provinceSearchTerm = '';
    this.filteredProvinceOptions = [];
    this.districtSearchTerm = '';
    this.filteredDistrictOptions = [];
    this.localLevelSearchTerm = '';
    this.filteredLocalLevelOptions = [];
    this.localLevelSearchOptions = [];
    this.otherParameterDropdownOpen = false;
    this.generatorForm.reset(createManageGeneratorFormValue(), { emitEvent: false });
    this.resetContactDetailRows();
    this.districtOptions = [];
    this.clearLocalLevels();
    this.applyFuelTypeVisibility(null);
    this.formSubmitted = false;
  }

  protected closeDeleteModal(): void {
    this.deleteModalOpen = false;
    this.selectedGenerator = null;
  }

  protected openGeneratorContactsModal(generator: ManageGeneratorItem): void {
    this.isGeneratorActionMenuOpen = false;
    this.selectedGeneratorContacts = generator;
    this.contactModalOpen = true;
  }

  protected closeGeneratorContactsModal(): void {
    this.contactModalOpen = false;
    this.selectedGeneratorContacts = null;
    this.isCreatingContactCredentials = false;
  }

  protected toggleGeneratorActionMenu(): void {
    if (this.isLoading) {
      return;
    }

    this.isGeneratorActionMenuOpen = !this.isGeneratorActionMenuOpen;
  }

  protected exportGeneratorData(): void {
    this.isGeneratorActionMenuOpen = false;
    this.isExporting = true;
    const query = this.buildQueryString({
      search: this.filterForm.get('search')?.value,
      substation_id: this.filterForm.get('substation_id')?.value,
      province_id: this.filterForm.get('province_id')?.value,
      owner: this.filterForm.get('owner')?.value,
      generator_type: this.filterForm.get('generator_type')?.value,
      fuel_type: this.filterForm.get('fuel_type')?.value,
      other_parameter: this.getSelectedOtherParameterFilterValue(),
      page_no: this.pagination.page_no,
      page_size: this.pagination.page_size,
    });

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_GENERATORS, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildGeneratorFileName('generators_export')),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || GENERATOR_FAILED_EXPORT_MESSAGE),
      });
  }

  protected downloadGeneratorTemplate(): void {
    this.isGeneratorActionMenuOpen = false;
    this.isDownloadingTemplate = true;
    this.httpSvc
      .downloadfile(AuthEndPoints.GENERATOR_TEMPLATE, ApiMethod.GET)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isDownloadingTemplate = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, 'generator_import_template.xlsx'),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || GENERATOR_FAILED_TEMPLATE_MESSAGE),
      });
  }

  protected triggerGeneratorImportFilePicker(fileInput: HTMLInputElement | null): void {
    if (!fileInput || this.isLoading || this.isExporting || this.isDownloadingTemplate || this.isImporting) {
      return;
    }

    this.isGeneratorActionMenuOpen = false;
    fileInput.click();
  }

  protected onGeneratorImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.length ? input.files[0] : null;

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.commonSvc.showErrorToastr(GENERATOR_ONLY_XLSX_MESSAGE);
      if (input) {
        input.value = '';
      }
      return;
    }

    this.isImporting = true;
    const formData = new FormData();
    formData.append('file', file);

    this.httpSvc
      .requestCall(AuthEndPoints.IMPORT_GENERATORS, ApiMethod.POST, formData, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isImporting = false;
          if (input) {
            input.value = '';
          }
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => this.loadGenerators(this.pagination.page_no),
      });
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.length ? input.files[0] : null;
    this.setSelectedFile(file);
  }

  protected removeSelectedFile(fileInput?: HTMLInputElement | null): void {
    if (this.modalMode === 'view') {
      return;
    }
    this.setSelectedFile(null);
    this.existingFilePath = null;
    this.filePathCleared = this.modalMode === 'edit';
    if (fileInput) {
      fileInput.value = '';
    }
  }

  protected previewSelectedFile(): void {
    const previewUrl = this.getFilePreviewUrl();
    if (!previewUrl || typeof window === 'undefined') {
      return;
    }

    window.open(previewUrl, '_blank', 'noopener');
  }

  protected hasFilePreview(): boolean {
    return Boolean(this.getFilePreviewUrl());
  }

  protected getFilePreviewLabel(): string {
    if (this.selectedFileName) {
      return this.selectedFileName;
    }

    if (this.existingFilePath) {
      return this.getFileName(this.existingFilePath);
    }

    return '';
  }

  protected getDisplayedFileLabel(): string {
    const fileName = this.getFilePreviewLabel();
    if (this.modalMode === 'create' || fileName.length <= GENERATOR_FILE_NAME_DISPLAY_MAX_LENGTH) {
      return fileName;
    }

    const extensionIndex = fileName.lastIndexOf('.');
    const extension = extensionIndex > 0 ? fileName.slice(extensionIndex) : '';
    const availableNameLength = GENERATOR_FILE_NAME_DISPLAY_MAX_LENGTH - extension.length - 3;
    return `${fileName.slice(0, Math.max(availableNameLength, 1))}...${extension}`;
  }

  protected hasAnyFile(): boolean {
    return Boolean(this.selectedFile || this.existingFilePath);
  }

  private setSelectedFile(file: File | null): void {
    if (this.selectedFilePreviewUrl) {
      URL.revokeObjectURL(this.selectedFilePreviewUrl);
      this.selectedFilePreviewUrl = null;
    }

    this.selectedFile = file;
    this.selectedFileName = file ? file.name : '';
    this.selectedFilePreviewUrl = file ? URL.createObjectURL(file) : null;
    if (file) {
      this.filePathCleared = false;
    }
  }

  protected saveGenerator(): void {
    if (this.modalMode === 'view') {
      return;
    }
    if (this.generatorForm.invalid) {
      this.formSubmitted = true;
      this.submitErrorMessage = GENERATOR_REQUIRED_FIELDS_MESSAGE;
      this.generatorForm.markAllAsTouched();
      return;
    }

    this.submitErrorMessage = '';
    const payload = this.buildGeneratorPayload(null);
    const apiRoute = this.modalMode === 'create' ? AuthEndPoints.ADD_GENERATOR : AuthEndPoints.EDIT_GENERATOR;
    const method = this.modalMode === 'create' ? ApiMethod.POST : ApiMethod.PATCH;
    this.saveGeneratorRecord(apiRoute, method, payload);
  }

  private saveGeneratorRecord(apiRoute: string, method: ApiMethod, payload: ManageGeneratorPayload): void {
    this.httpSvc
      .requestCall(apiRoute, method, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.handleGeneratorSaveSuccess(res),
        error: (error) => {
          this.submitErrorMessage = this.getApiErrorMessage(error) || GENERATOR_FAILED_SAVE_MESSAGE;
        },
      });
  }

  private handleGeneratorSaveSuccess(response: unknown): void {
    const generatorId = this.extractGeneratorId(response) ?? this.selectedGenerator?.id ?? null;

    if (!this.selectedFile || generatorId === null) {
      this.closeAndReloadGenerators();
      return;
    }

    this.uploadAndAttachGeneratorFile(generatorId);
  }

  private uploadAndAttachGeneratorFile(generatorId: number): void {
    const file = this.selectedFile;
    if (!file) {
      this.closeAndReloadGenerators();
      return;
    }

    this.uploadGeneratorFile(file)
      .pipe(
        switchMap((uploadRes) => {
          const filePath = this.extractFilePath(uploadRes);
          if (!filePath) {
            return of(null);
          }

          return this.httpSvc.requestCall(AuthEndPoints.EDIT_GENERATOR, ApiMethod.PATCH, {
            id: generatorId,
            file_path: filePath,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.closeAndReloadGenerators(),
        error: (error) => {
          this.submitErrorMessage = this.getApiErrorMessage(error) || GENERATOR_FAILED_ATTACHMENT_MESSAGE;
          this.closeAndReloadGenerators();
        },
      });
  }

  private closeAndReloadGenerators(): void {
    this.closeGeneratorModal();
    this.loadGenerators(this.pagination.page_no);
  }

  protected confirmDelete(): void {
    if (!this.selectedGenerator) {
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.DELETE_GENERATOR, ApiMethod.DELETE, { id: this.selectedGenerator.id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          this.loadGenerators(this.pagination.page_no);
        },
      });
  }

  protected onGeneratorStatusChange(generator: ManageGeneratorItem, nextStatusValue: string): void {
    const nextStatus = this.normalizeStatus(nextStatusValue);
    if (!generator.id || nextStatus === this.normalizeStatus(generator.status)) {
      return;
    }

    generator.status = nextStatus;
    this.httpSvc
      .requestCall(AuthEndPoints.EDIT_GENERATOR, ApiMethod.PATCH, {
        id: generator.id,
        status: nextStatus,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loadGenerators(this.pagination.page_no);
        },
        error: (error) => {
          console.error(GENERATOR_FAILED_UPDATE_STATUS_MESSAGE, error);
          this.loadGenerators(this.pagination.page_no);
        },
      });
  }

  protected trackByGeneratorId(_index: number, item: ManageGeneratorItem): number {
    return item.id;
  }

  protected toggleSort(field: ManageGeneratorSortField): void {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.loadGenerators(1);
  }

  protected isSortAscending(field: ManageGeneratorSortField): boolean { return this.sortBy === field && this.sortOrder === 'asc'; }
  protected isSortDescending(field: ManageGeneratorSortField): boolean { return this.sortBy === field && this.sortOrder === 'desc'; }

  protected trackByGeneratorContactId(index: number, item: ManageGeneratorContactDetailItem): number {
    return item.id || index;
  }

  protected getGeneratorContactRows(generator: ManageGeneratorItem | null): ManageGeneratorContactDetailItem[] {
    if (!generator) {
      return [];
    }

    const contactRows = generator.contact_details ?? [];
    if (contactRows.length) {
      return contactRows;
    }

    if (
      generator.contact_person_name ||
      generator.contact_email ||
      generator.contact_mobile_no ||
      generator.contact_designation
    ) {
      return [
        {
          id: 0,
          generator_id: generator.id,
          person_name: generator.contact_person_name ?? '',
          username: null,
          email: generator.contact_email ?? '',
          mobile_no: generator.contact_mobile_no ?? '',
          designation: generator.contact_designation ?? null,
          is_portal_user: false,
          user_id: null,
        },
      ];
    }

    return [];
  }

  protected getGeneratorContactCount(generator: ManageGeneratorItem): number {
    return this.getGeneratorContactRows(generator).length;
  }

  protected hasPortalUserAccess(contact: ManageGeneratorContactDetailItem): boolean {
    return Boolean(contact.is_portal_user);
  }

  protected createContactLoginCredentials(contact: ManageGeneratorContactDetailItem): void {
    const generator = this.selectedGeneratorContacts;
    if (!generator || this.isCreatingContactCredentials || this.hasPortalUserAccess(contact) || !contact.id) {
      return;
    }

    this.isCreatingContactCredentials = true;
    this.httpSvc
      .requestCall(
        AuthEndPoints.CREATE_GENERATOR_CONTACT_CREDENTIALS,
        ApiMethod.POST,
        {
          generator_id: generator.id,
          contact_id: contact.id,
        },
        true,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isCreatingContactCredentials = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          const createdUserId = Number(response?.value?.user_id ?? response?.user_id ?? null);
          let createdUsername: string | null = null;
          if (typeof response?.value?.username === 'string') {
            createdUsername = response.value.username;
          } else if (typeof response?.username === 'string') {
            createdUsername = response.username;
          }
          contact.is_portal_user = true;
          contact.user_id = Number.isFinite(createdUserId) ? createdUserId : contact.user_id ?? null;
          contact.username = createdUsername || contact.username || null;
        },
      });
  }

  protected isRequiredFieldInvalid(fieldName: string): boolean {
    const control = this.generatorForm.get(fieldName);
    return Boolean(control?.hasError('required') && (control.touched || this.formSubmitted));
  }

  protected getRequiredFieldError(fieldName: string, message: string): string {
    return this.isRequiredFieldInvalid(fieldName) ? message : '\u00A0';
  }

  protected canShowPagination(): boolean {
    return canShowPagination(this.pagination.total_records);
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(this.pagination, GENERATOR_NO_RECORDS_LABEL, GENERATOR_SHOWING_RESULTS_LABEL, GENERATOR_RESULTS_LABEL);
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected goToPreviousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected goToNextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }

    this.loadGenerators(pageNo);
  }

  protected isActiveStatus(status: string | null): boolean {
    return (status ?? '').toUpperCase() === 'ACTIVE';
  }

  protected getSubstationName(id: number | null): string {
    if (id === null || id === undefined) {
      return '-';
    }

    return this.substationOptions.find((item) => item.id === id)?.name ?? '-';
  }

  protected getEntityName(id: number | null): string {
    if (id === null || id === undefined) {
      return '-';
    }

    return this.entityOptions.find((item) => item.id === id)?.name ?? '-';
  }

  protected onEntitySearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.entitySearchTerm = value;
    this.filterEntityAutocomplete(value);

    const exactMatch = this.findEntityByName(value);
    if (exactMatch) {
      this.generatorForm.patchValue(
        {
          entity_id: exactMatch.id,
          promoter_company: exactMatch.name,
        },
        { emitEvent: false },
      );
      return;
    }

    this.generatorForm.patchValue(
      {
        entity_id: null,
        promoter_company: '',
      },
      { emitEvent: false },
    );
  }

  protected onEntitySearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitEntityAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitEntityAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onProvinceSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.provinceSearchTerm = value;
    this.filterProvinceAutocomplete(value);

    const exactMatch = this.findProvinceByName(value);
    if (exactMatch) {
      this.generatorForm.patchValue(
        {
          province_id: exactMatch.id,
          district_id: null,
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.onProvinceSelectionChange(exactMatch.id);
      return;
    }

    this.generatorForm.patchValue(
      {
        province_id: null,
        district_id: null,
        local_level_id: null,
        local_level: '',
      },
      { emitEvent: false },
    );
  }

  protected onProvinceSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitProvinceAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitProvinceAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onSubstationSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.substationSearchTerm = value;
    this.filterSubstationAutocomplete(value);

    const exactMatch = this.findSubstationByName(value);
    if (exactMatch) {
      this.generatorForm.patchValue(
        {
          substation_id: exactMatch.id,
        },
        { emitEvent: false },
      );
      return;
    }

    this.generatorForm.patchValue(
      {
        substation_id: null,
      },
      { emitEvent: false },
    );
  }

  protected onSubstationSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitSubstationAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitSubstationAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onFilterSubstationSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.filterSubstationSearchTerm = value;
    this.filterFilterSubstationAutocomplete(value);

    if (!value.trim()) {
      this.clearFilterSubstationSelection();
      return;
    }

    const match = this.findSubstationByName(value);
    if (match) {
      this.applyFilterSubstationSelection(match);
    }
  }

  protected onFilterSubstationSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitFilterSubstationAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitFilterSubstationAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onDistrictSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.districtSearchTerm = value;
    this.filterDistrictAutocomplete(value);

    const exactMatch = this.findDistrictByName(value);
    if (exactMatch) {
      this.generatorForm.patchValue(
        {
          district_id: exactMatch.id,
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.onDistrictSelectionChange(exactMatch.id);
      return;
    }

    this.generatorForm.patchValue(
      {
        district_id: null,
        local_level_id: null,
        local_level: '',
      },
      { emitEvent: false },
    );
  }

  protected onDistrictSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitDistrictAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitDistrictAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onFilterProvinceSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.filterProvinceSearchTerm = value;
    this.filterFilterProvinceAutocomplete(value);

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      this.clearFilterDependentSelections();
      this.loadGenerators(1);
      return;
    }

    const match = this.findProvinceByName(normalizedValue);
    if (!match) {
      return;
    }

    this.filterForm.patchValue(
      {
        province_id: match.id,
        district_id: GENERATOR_ALL_FILTER_VALUE,
        local_level_id: GENERATOR_ALL_FILTER_VALUE,
      },
      { emitEvent: false },
    );
    this.filterDistrictSearchTerm = '';
    this.filterLocalLevelSearchTerm = '';
    this.clearFilterDistrictOptions();
    this.clearFilterLocalLevelOptions();
    this.loadFilterDistricts(match.id, null);
    this.loadGenerators(1);
  }

  protected onFilterProvinceSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitFilterProvinceAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitFilterProvinceAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onFilterDistrictSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.filterDistrictSearchTerm = value;
    this.filterFilterDistrictAutocomplete(value);

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      this.filterForm.patchValue({ local_level_id: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
      this.filterLocalLevelSearchTerm = '';
      this.clearFilterLocalLevelOptions();
      this.loadGenerators(1);
      return;
    }

    const match = this.findDistrictByName(normalizedValue);
    if (!match) {
      return;
    }

    this.filterForm.patchValue(
      {
        district_id: match.id,
        local_level_id: GENERATOR_ALL_FILTER_VALUE,
      },
      { emitEvent: false },
    );
    this.filterLocalLevelSearchTerm = '';
    this.clearFilterLocalLevelOptions();
    this.loadFilterLocalLevels(match.id, null);
    this.loadGenerators(1);
  }

  protected onFilterDistrictSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitFilterDistrictAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitFilterDistrictAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected onFilterLocalLevelSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.filterLocalLevelSearchTerm = value;
    this.filterFilterLocalLevelAutocomplete(value);

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      const currentLocalLevel = this.filterForm.get('local_level_id')?.value;
      if (currentLocalLevel !== GENERATOR_ALL_FILTER_VALUE) {
        this.filterForm.patchValue({ local_level_id: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
        this.loadGenerators(1);
      }
      return;
    }

    const match = this.findLocalLevelByName(normalizedValue);
    if (!match) {
      return;
    }

    if (this.filterForm.get('local_level_id')?.value !== match.id) {
      this.filterForm.patchValue({ local_level_id: match.id }, { emitEvent: false });
      this.loadGenerators(1);
    }
  }

  protected onFilterLocalLevelSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitFilterLocalLevelAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitFilterLocalLevelAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  private applyFilterSubstationSelection(substation: ManageGeneratorLookupOption): void {
    const selectedSubstationId = this.filterForm.get('substation_id')?.value;
    this.filterSubstationSearchTerm = substation.name;
    this.filterFilterSubstationAutocomplete(substation.name);
    if (selectedSubstationId === substation.id) {
      return;
    }

    this.filterForm.patchValue({ substation_id: substation.id }, { emitEvent: false });
    this.loadGenerators(1);
  }

  private clearFilterSubstationSelection(): void {
    const selectedSubstationId = this.filterForm.get('substation_id')?.value;
    this.filterSubstationSearchTerm = '';
    this.filterFilterSubstationAutocomplete('');
    if (selectedSubstationId === GENERATOR_ALL_FILTER_VALUE) {
      return;
    }

    this.filterForm.patchValue({ substation_id: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
    this.loadGenerators(1);
  }

  private commitFilterSubstationAutocompleteValue(): void {
    const selectedSubstation = this.findSubstationByName(this.filterSubstationSearchTerm);
    if (!selectedSubstation) {
      this.clearFilterSubstationSelection();
      return;
    }

    this.applyFilterSubstationSelection(selectedSubstation);
  }

  private commitFilterProvinceAutocompleteValue(): void {
    const trimmedValue = this.filterProvinceSearchTerm.trim();
    if (!trimmedValue) {
      const provinceValue = this.filterForm.get('province_id')?.value;
      const districtValue = this.filterForm.get('district_id')?.value;
      const localValue = this.filterForm.get('local_level_id')?.value;
      if (provinceValue !== GENERATOR_ALL_FILTER_VALUE || districtValue !== GENERATOR_ALL_FILTER_VALUE || localValue !== GENERATOR_ALL_FILTER_VALUE) {
        this.clearFilterDependentSelections();
        this.loadGenerators(1);
      }
      return;
    }

    const matchedProvince = this.findFilterProvinceByName(trimmedValue);
    if (!matchedProvince) {
      this.clearFilterDependentSelections();
      this.loadGenerators(1);
      return;
    }

    const districtChanged =
      this.filterForm.get('province_id')?.value !== matchedProvince.id ||
      this.filterForm.get('district_id')?.value !== GENERATOR_ALL_FILTER_VALUE ||
      this.filterForm.get('local_level_id')?.value !== GENERATOR_ALL_FILTER_VALUE;

    this.filterProvinceSearchTerm = matchedProvince.name;
    this.filterForm.patchValue(
      {
        province_id: matchedProvince.id,
        district_id: GENERATOR_ALL_FILTER_VALUE,
        local_level_id: GENERATOR_ALL_FILTER_VALUE,
      },
      { emitEvent: false },
    );
    this.filterDistrictSearchTerm = '';
    this.filterLocalLevelSearchTerm = '';
    this.loadFilterDistricts(matchedProvince.id);
    this.clearFilterLocalLevelOptions();

    if (districtChanged) {
      this.loadGenerators(1);
    }
  }

  private commitFilterDistrictAutocompleteValue(): void {
    const trimmedValue = this.filterDistrictSearchTerm.trim();
    if (!trimmedValue) {
      const districtValue = this.filterForm.get('district_id')?.value;
      const localValue = this.filterForm.get('local_level_id')?.value;
      if (districtValue !== GENERATOR_ALL_FILTER_VALUE || localValue !== GENERATOR_ALL_FILTER_VALUE) {
        this.filterForm.patchValue(
          {
            district_id: GENERATOR_ALL_FILTER_VALUE,
            local_level_id: GENERATOR_ALL_FILTER_VALUE,
          },
          { emitEvent: false },
        );
        this.filterLocalLevelSearchTerm = '';
        this.clearFilterLocalLevelOptions();
        this.loadGenerators(1);
      }
      return;
    }

    const matchedDistrict = this.findFilterDistrictByName(trimmedValue);
    if (!matchedDistrict) {
      this.filterForm.patchValue(
        {
          district_id: GENERATOR_ALL_FILTER_VALUE,
          local_level_id: GENERATOR_ALL_FILTER_VALUE,
        },
        { emitEvent: false },
      );
      this.filterDistrictSearchTerm = '';
      this.filterLocalLevelSearchTerm = '';
      this.clearFilterLocalLevelOptions();
      this.loadGenerators(1);
      return;
    }

    const localChanged =
      this.filterForm.get('district_id')?.value !== matchedDistrict.id ||
      this.filterForm.get('local_level_id')?.value !== GENERATOR_ALL_FILTER_VALUE;

    this.filterDistrictSearchTerm = matchedDistrict.name;
    this.filterForm.patchValue(
      {
        district_id: matchedDistrict.id,
        local_level_id: GENERATOR_ALL_FILTER_VALUE,
      },
      { emitEvent: false },
    );
    this.filterLocalLevelSearchTerm = '';
    this.loadFilterLocalLevels(matchedDistrict.id);
    this.clearFilterLocalLevelOptions();

    if (localChanged) {
      this.loadGenerators(1);
    }
  }

  private commitFilterLocalLevelAutocompleteValue(): void {
    const trimmedValue = this.filterLocalLevelSearchTerm.trim();
    if (!trimmedValue) {
      if (this.filterForm.get('local_level_id')?.value !== GENERATOR_ALL_FILTER_VALUE) {
        this.filterForm.patchValue({ local_level_id: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
        this.loadGenerators(1);
      }
      return;
    }

    const matchedLocalLevel = this.findFilterLocalLevelByName(trimmedValue);
    if (!matchedLocalLevel) {
      this.filterForm.patchValue({ local_level_id: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
      this.filterLocalLevelSearchTerm = '';
      this.loadGenerators(1);
      return;
    }

    if (this.filterForm.get('local_level_id')?.value !== matchedLocalLevel.id) {
      this.filterForm.patchValue({ local_level_id: matchedLocalLevel.id }, { emitEvent: false });
      this.filterLocalLevelSearchTerm = matchedLocalLevel.name ?? '';
      this.loadGenerators(1);
      return;
    }

    this.filterLocalLevelSearchTerm = matchedLocalLevel.name ?? '';
  }

  private clearFilterDependentSelections(): void {
    this.filterForm.patchValue(
      {
        province_id: GENERATOR_ALL_FILTER_VALUE,
        district_id: GENERATOR_ALL_FILTER_VALUE,
        local_level_id: GENERATOR_ALL_FILTER_VALUE,
      },
      { emitEvent: false },
    );
    this.filterProvinceSearchTerm = '';
    this.filterDistrictSearchTerm = '';
    this.filterLocalLevelSearchTerm = '';
    this.clearFilterDistrictOptions();
    this.clearFilterLocalLevelOptions();
  }

  private clearFilterDistrictOptions(): void {
    this.filterDistrictOptions = [];
    this.filteredFilterDistrictOptions = [];
  }

  private clearFilterLocalLevelOptions(): void {
    this.filterLocalLevelOptions = [];
    this.filteredFilterLocalLevelOptions = [];
  }

  protected getProvinceName(id: number): string {
    return this.provinceOptions.find((item) => item.id === id)?.name ?? '-';
  }

  protected getDistrictName(id: number): string {
    return this.districtOptions.find((item) => item.id === id)?.name ?? '-';
  }

  protected getLocalLevelName(id: number | null): string {
    if (id === null) {
      return '-';
    }

    return this.localLevelOptions.find((item) => item.id === id)?.local_name ?? '-';
  }

  protected getVoltageLevelLabel(id: number | null): string {
    if (id === null) {
      return '-';
    }

    return this.voltageLevelOptions.find((item) => item.id === id)?.name ?? '-';
  }

  protected getFileName(filePath: string | null): string {
    if (!filePath) {
      return '-';
    }

    const segments = filePath.split('/');
    return segments[segments.length - 1] || filePath;
  }

  protected normalizeStatus(status: string | null): string {
    return (status ?? 'ACTIVE').toUpperCase();
  }

  protected normalizeSourceType(value: string | null | undefined): string {
    const normalizedValue = this.normalizeTextValue(value);
    if (!normalizedValue) {
      return '';
    }

    return normalizedValue
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  protected normalizeFuelTypeOptionValue(value: string | null | undefined): string {
    const normalizedValue = this.normalizeTextValue(value);
    if (!normalizedValue) {
      return '';
    }

    const matchedOption = this.fuelTypeOptions.find((option) => {
      const optionValue = String(option.value ?? '').trim().toLowerCase();
      const optionLabel = String(option.label ?? '').trim().toLowerCase();
      const candidate = normalizedValue.trim().toLowerCase();
      return optionValue === candidate || optionLabel === candidate;
    });

    return matchedOption?.value ?? normalizedValue;
  }

  private waitForEntityOptions(): Promise<void> {
    if (this.entityOptionsLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.entityOptionsLoadWaiters.push(resolve);
    });
  }

  private markEntityOptionsLoaded(): void {
    this.entityOptionsLoaded = true;
    while (this.entityOptionsLoadWaiters.length) {
      const resolve = this.entityOptionsLoadWaiters.shift();
      resolve?.();
    }
  }

  protected getFuelTypeOptionsForCurrentType(): readonly { label: string; value: string }[] {
    return this.normalizeStatus(this.generatorForm.get('generator_type')?.value ?? '') === 'HYDRO'
      ? this.fuelTypeOptions
      : [];
  }

  protected getFilterFuelTypeOptionsForCurrentType(): readonly { label: string; value: string }[] {
    if (this.normalizeStatus(this.filterForm.get('generator_type')?.value ?? '') !== 'HYDRO') {
      return [{ label: 'All Fuel Types', value: GENERATOR_ALL_FILTER_VALUE }];
    }

    return [
      { label: 'All Fuel Types', value: GENERATOR_ALL_FILTER_VALUE },
      ...this.fuelTypeOptions,
    ];
  }

  protected isHydroFilterSelected(): boolean {
    return this.normalizeStatus(this.filterForm.get('generator_type')?.value ?? '') === 'HYDRO';
  }

  protected getGeneratorTableColspan(): number {
    return this.isFuelTypeColumnVisible() ? 12 : 11;
  }

  protected isFuelTypeColumnVisible(): boolean {
    const sourceType = this.normalizeStatus(this.filterForm.get('generator_type')?.value ?? '');
    return sourceType === 'HYDRO' || sourceType === this.normalizeStatus(this.allFilterValue);
  }

  protected onScadaStationCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const sanitized = input.value.replace(/[^a-zA-Z0-9_-]/g, '');
    if (sanitized !== input.value) {
      input.value = sanitized;
    }

    this.generatorForm.patchValue(
      {
        scada_station_code: sanitized,
      },
      { emitEvent: false },
    );
  }

  protected onContactMobileNumberInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const mobileNumber = input.value.replace(/\D/g, '').slice(0, 10);
    if (input.value !== mobileNumber) {
      input.value = mobileNumber;
    }

    this.contactDetailsArray.at(index)?.get('mobile_no')?.setValue(mobileNumber);
  }

  protected isContactDetailPortalUser(index: number): boolean {
    return Boolean(this.contactDetailsArray.at(index)?.get('is_portal_user')?.value);
  }

  protected onLocalLevelSearchInput(event: ManageGeneratorAutocompleteEvent): void {
    const value = this.extractAutocompleteValue(event);
    this.localLevelSearchTerm = value;
    this.filterLocalLevelAutocomplete(value);

    const exactMatch = this.findLocalLevelByName(value);
    if (exactMatch) {
      this.generatorForm.patchValue(
        {
          local_level_id: exactMatch.id,
          local_level: exactMatch.name ?? '',
        },
        { emitEvent: false },
      );
      return;
    }

    this.generatorForm.patchValue(
      {
        local_level_id: null,
        local_level: '',
      },
      { emitEvent: false },
    );
  }

  protected onLocalLevelSearchBlur(): void {
    if (typeof window === 'undefined') {
      this.commitLocalLevelAutocompleteValue();
      return;
    }

    window.setTimeout(() => {
      this.commitLocalLevelAutocompleteValue();
      this.cdr.markForCheck();
    }, 140);
  }

  protected getOtherParameterDisplayValue(): string {
    const selections = this.generatorForm.get('other_parameter')?.value;
    if (!Array.isArray(selections) || !selections.length) {
      return '';
    }

    return selections.join(', ');
  }

  protected formatOtherParameterForDisplay(value: string | null): string {
    return value ? value.replace(/\bKathmanduLoad\b/g, 'Kathmandu Load') : '-';
  }

  protected toggleOtherParameterDropdown(): void {
    if (this.modalMode === 'view') {
      return;
    }

    this.otherParameterDropdownOpen = !this.otherParameterDropdownOpen;
  }

  protected closeOtherParameterDropdown(): void {
    this.otherParameterDropdownOpen = false;
  }

  protected isOtherParameterSelected(option: string): boolean {
    const selections = this.generatorForm.get('other_parameter')?.value;
    return Array.isArray(selections) ? selections.includes(option) : false;
  }

  protected onOtherParameterSelection(option: string, checked: boolean): void {
    if (this.modalMode === 'view') {
      return;
    }

    const control = this.generatorForm.get('other_parameter');
    const selections = Array.isArray(control?.value) ? [...control.value] : [];

    if (checked) {
      if (!selections.includes(option)) {
        selections.push(option);
      }
    } else {
      const index = selections.indexOf(option);
      if (index >= 0) {
        selections.splice(index, 1);
      }
    }

    control?.setValue(selections, { emitEvent: false });
  }

  protected getOtherParameterFilterDisplayValue(): string {
    const selections = this.getSelectedOtherParameterFilterValues();
    if (!selections.length) {
      return '';
    }

    return this.otherParameterFilterOptions
      .filter((option) => selections.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }

  protected toggleOtherParameterFilterDropdown(): void {
    this.otherParameterFilterDropdownOpen = !this.otherParameterFilterDropdownOpen;
  }

  protected isOtherParameterFilterSelected(option: string): boolean {
    return this.getSelectedOtherParameterFilterValues().includes(option);
  }

  protected onOtherParameterFilterSelection(option: string, checked: boolean): void {
    const control = this.filterForm.get('other_parameter');
    const selections = this.getSelectedOtherParameterFilterValues();
    const nextSelections = checked
      ? [...new Set([...selections, option])]
      : selections.filter((selection) => selection !== option);
    control?.setValue(nextSelections);
  }

  protected onFilterGeneratorTypeChange(generatorType: string | null | undefined): void {
    if (this.normalizeStatus(generatorType ?? '') !== 'HYDRO') {
      if (this.filterForm.get('fuel_type')?.value !== GENERATOR_ALL_FILTER_VALUE) {
        this.filterForm.patchValue({ fuel_type: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
      }
      return;
    }

    if (this.filterForm.get('fuel_type')?.value === null || this.filterForm.get('fuel_type')?.value === undefined) {
      this.filterForm.patchValue({ fuel_type: GENERATOR_ALL_FILTER_VALUE }, { emitEvent: false });
    }
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target || this.hostElement.nativeElement.contains(target)) {
      return;
    }

    this.otherParameterDropdownOpen = false;
    this.otherParameterFilterDropdownOpen = false;
  }

  private getSelectedOtherParameterFilterValues(): string[] {
    const value = this.filterForm.get('other_parameter')?.value;
    return Array.isArray(value) ? value : [];
  }

  private getSelectedOtherParameterFilterValue(): string | null {
    const values = this.getSelectedOtherParameterFilterValues();
    return values.length ? values.join(',') : null;
  }

  private createContactDetailGroup(detail?: Partial<ManageGeneratorContactDetailFormValue>): UntypedFormGroup {
    return this.fb.group({
      id: [detail?.id ?? null],
      person_name: [detail?.person_name ?? '', [Validators.pattern(CONTACT_PERSON_PATTERN), Validators.maxLength(255)]],
      email: [detail?.email ?? '', [Validators.pattern(EMAIL_PATTERN), Validators.maxLength(255)]],
      mobile_no: [this.normalizeContactMobileNumber(detail?.mobile_no), [Validators.pattern(MOBILE_NUMBER_PATTERN), Validators.maxLength(10)]],
      designation: [detail?.designation ?? '', [Validators.maxLength(255)]],
      is_portal_user: [Boolean(detail?.is_portal_user)],
    });
  }

  private isContactDetailPatternInvalid(index: number, fieldName: string): boolean {
    const control = this.contactDetailsArray.at(index)?.get(fieldName);
    return Boolean(control?.hasError('pattern') && (control.touched || this.formSubmitted));
  }

  private normalizeContactMobileNumber(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    return '';
  }

  private applyFuelTypeVisibility(generatorType: string | null | undefined): void {
    if (this.normalizeStatus(generatorType ?? '') !== 'HYDRO') {
      this.generatorForm.patchValue(
        {
          fuel_type: '',
        },
        { emitEvent: false },
      );
    }
  }

  private splitCommaSeparatedValues(value: string | null | undefined): string[] {
    if (!value) {
      return [];
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => Boolean(item) && this.otherParameterOptions.includes(item));
  }

  private joinCommaSeparatedValues(values: string[] | null | undefined): string | null {
    if (!Array.isArray(values) || !values.length) {
      return null;
    }

    return values.join(', ');
  }

  private resetContactDetailRows(details: Array<Partial<ManageGeneratorContactDetailFormValue>> = []): void {
    while (this.contactDetailsArray.length > 0) {
      this.contactDetailsArray.removeAt(0);
    }

    if (details.length) {
      details.forEach((detail) => {
        if (this.modalMode === 'view') {
          this.contactDetailsArray.push(this.createContactDetailGroup(detail));
          return;
        }

        this.addContactDetailRow(detail);
      });
      return;
    }

    if (this.modalMode !== 'view') {
      this.addContactDetailRow();
    }
  }

  private normalizeContactDetails(generator: ManageGeneratorItem): ManageGeneratorContactDetailFormValue[] {
    const contactRows: ManageGeneratorContactDetailFormValue[] = (generator.contact_details ?? []).map((contact) => ({
      id: contact.id,
      person_name: contact.person_name ?? '',
      username: contact.username ?? null,
      email: contact.email ?? '',
      mobile_no: contact.mobile_no ?? '',
      designation: contact.designation ?? '',
      is_portal_user: Boolean(contact.is_portal_user),
    }));

    if (!contactRows.length) {
      contactRows.push({
        id: null,
        person_name: generator.contact_person_name ?? '',
        username: null,
        email: generator.contact_email ?? '',
        mobile_no: generator.contact_mobile_no ?? '',
        designation: generator.contact_designation ?? '',
        is_portal_user: false,
      });
    }

    return contactRows;
  }

  private syncEntityAutocompleteValue(entityId: number | null, fallbackLabel = ''): void {
    const resolvedName = entityId !== null && entityId !== undefined ? this.getEntityName(entityId) : '';
    const entityName = resolvedName !== '-' ? resolvedName : fallbackLabel;
    this.entitySearchTerm = entityName ?? '';
    this.generatorForm.patchValue(
      {
        promoter_company: entityName ?? '',
      },
      { emitEvent: false },
    );
    this.filterEntityAutocomplete(this.entitySearchTerm);
  }

  private syncSubstationAutocompleteValue(substationId: number | null, fallbackLabel = ''): void {
    const resolvedName = substationId !== null && substationId !== undefined ? this.getSubstationName(substationId) : '';
    const substationName = resolvedName !== '-' ? resolvedName : fallbackLabel;
    this.substationSearchTerm = substationName ?? '';
    this.generatorForm.patchValue(
      {
        substation_id: substationId,
      },
      { emitEvent: false },
    );
    this.filterSubstationAutocomplete(this.substationSearchTerm);
  }

  private commitEntityAutocompleteValue(): void {
    const trimmedValue = this.entitySearchTerm.trim();
    if (!trimmedValue) {
      this.generatorForm.patchValue(
        {
          entity_id: null,
          promoter_company: '',
        },
        { emitEvent: false },
      );
      this.entitySearchTerm = '';
      return;
    }

    const matchedEntity = this.findEntityByName(trimmedValue);
    if (!matchedEntity) {
      this.generatorForm.patchValue(
        {
          entity_id: null,
          promoter_company: '',
        },
        { emitEvent: false },
      );
      this.entitySearchTerm = '';
      this.filteredEntityOptions = [...this.entityOptions];
      return;
    }

    this.generatorForm.patchValue(
      {
        entity_id: matchedEntity.id,
        promoter_company: matchedEntity.name,
      },
      { emitEvent: false },
    );
    this.entitySearchTerm = matchedEntity.name;
    this.filterEntityAutocomplete(matchedEntity.name);
  }

  private commitSubstationAutocompleteValue(): void {
    const trimmedValue = this.substationSearchTerm.trim();
    if (!trimmedValue) {
      this.generatorForm.patchValue(
        {
          substation_id: null,
        },
        { emitEvent: false },
      );
      this.substationSearchTerm = '';
      return;
    }

    const matchedSubstation = this.findSubstationByName(trimmedValue);
    if (!matchedSubstation) {
      this.generatorForm.patchValue(
        {
          substation_id: null,
        },
        { emitEvent: false },
      );
      this.substationSearchTerm = '';
      this.filteredSubstationOptions = [...this.substationOptions];
      return;
    }

    this.generatorForm.patchValue(
      {
        substation_id: matchedSubstation.id,
      },
      { emitEvent: false },
    );
    this.substationSearchTerm = matchedSubstation.name;
    this.filterSubstationAutocomplete(matchedSubstation.name);
  }

  private syncProvinceAutocompleteValue(provinceId: number | null, fallbackLabel = ''): void {
    const resolvedName = provinceId !== null && provinceId !== undefined ? this.getProvinceName(provinceId) : '';
    this.provinceSearchTerm = resolvedName !== '-' ? resolvedName : fallbackLabel;
    this.generatorForm.patchValue(
      {
        province_id: provinceId,
      },
      { emitEvent: false },
    );
    this.filterProvinceAutocomplete(this.provinceSearchTerm);
  }

  private syncDistrictAutocompleteValue(districtId: number | null, fallbackLabel = ''): void {
    const resolvedName = districtId !== null && districtId !== undefined ? this.getDistrictName(districtId) : '';
    this.districtSearchTerm = resolvedName !== '-' ? resolvedName : fallbackLabel;
    this.generatorForm.patchValue(
      {
        district_id: districtId,
      },
      { emitEvent: false },
    );
    this.filterDistrictAutocomplete(this.districtSearchTerm);
  }

  private syncLocalLevelAutocompleteValue(localLevelId: number | null, fallbackLabel = ''): void {
    const resolvedName = localLevelId !== null && localLevelId !== undefined ? this.getLocalLevelName(localLevelId) : '';
    this.localLevelSearchTerm = resolvedName !== '-' ? resolvedName : fallbackLabel;
    this.generatorForm.patchValue(
      {
        local_level_id: localLevelId,
        local_level: this.localLevelSearchTerm,
      },
      { emitEvent: false },
    );
    this.filterLocalLevelAutocomplete(this.localLevelSearchTerm);
  }

  private commitProvinceAutocompleteValue(): void {
    const trimmedValue = this.provinceSearchTerm.trim();
    if (!trimmedValue) {
      this.generatorForm.patchValue(
        {
          province_id: null,
          district_id: null,
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.provinceSearchTerm = '';
      this.districtSearchTerm = '';
      this.localLevelSearchTerm = '';
      this.districtOptions = [];
      this.filteredDistrictOptions = [];
      this.clearLocalLevels();
      return;
    }

    const matchedProvince = this.findProvinceByName(trimmedValue);
    if (!matchedProvince) {
      this.generatorForm.patchValue(
        {
          province_id: null,
          district_id: null,
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.provinceSearchTerm = '';
      this.districtSearchTerm = '';
      this.localLevelSearchTerm = '';
      this.districtOptions = [];
      this.filteredDistrictOptions = [];
      this.clearLocalLevels();
      return;
    }

    this.generatorForm.patchValue(
      {
        province_id: matchedProvince.id,
        district_id: null,
        local_level_id: null,
        local_level: '',
      },
      { emitEvent: false },
    );
    this.provinceSearchTerm = matchedProvince.name;
    this.districtSearchTerm = '';
    this.localLevelSearchTerm = '';
    this.districtOptions = [];
    this.filteredDistrictOptions = [];
    this.clearLocalLevels();
    this.loadDistricts(matchedProvince.id);
  }

  private commitDistrictAutocompleteValue(): void {
    const trimmedValue = this.districtSearchTerm.trim();
    if (!trimmedValue) {
      this.generatorForm.patchValue(
        {
          district_id: null,
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.districtSearchTerm = '';
      this.localLevelSearchTerm = '';
      this.filteredLocalLevelOptions = [];
      this.clearLocalLevels();
      return;
    }

    const matchedDistrict = this.findDistrictByName(trimmedValue);
    if (!matchedDistrict) {
      this.generatorForm.patchValue(
        {
          district_id: null,
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.districtSearchTerm = '';
      this.localLevelSearchTerm = '';
      this.filteredLocalLevelOptions = [];
      this.clearLocalLevels();
      return;
    }

    this.generatorForm.patchValue(
      {
        district_id: matchedDistrict.id,
        local_level_id: null,
        local_level: '',
      },
      { emitEvent: false },
    );
    this.districtSearchTerm = matchedDistrict.name;
    this.localLevelSearchTerm = '';
    this.filteredLocalLevelOptions = [];
    this.clearLocalLevels();
    this.loadLocalLevels(matchedDistrict.id);
  }

  private commitLocalLevelAutocompleteValue(): void {
    const trimmedValue = this.localLevelSearchTerm.trim();
    if (!trimmedValue) {
      this.generatorForm.patchValue(
        {
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.localLevelSearchTerm = '';
      return;
    }

    const matchedLocalLevel = this.findLocalLevelByName(trimmedValue);
    if (!matchedLocalLevel) {
      this.generatorForm.patchValue(
        {
          local_level_id: null,
          local_level: '',
        },
        { emitEvent: false },
      );
      this.localLevelSearchTerm = '';
      this.filteredLocalLevelOptions = [...this.localLevelSearchOptions];
      return;
    }

    this.generatorForm.patchValue(
      {
        local_level_id: matchedLocalLevel.id,
        local_level: matchedLocalLevel.name,
      },
      { emitEvent: false },
    );
    this.localLevelSearchTerm = matchedLocalLevel.name;
    this.filterLocalLevelAutocomplete(matchedLocalLevel.name);
  }

  private filterEntityAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredEntityOptions = [...this.entityOptions];
      return;
    }

    this.filteredEntityOptions = this.entityOptions.filter((entity) => entity.name.toLowerCase().includes(normalizedQuery));
  }

  private filterSubstationAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredSubstationOptions = [...this.substationOptions];
      return;
    }

    this.filteredSubstationOptions = this.substationOptions.filter((substation) => substation.name.toLowerCase().includes(normalizedQuery));
  }

  private filterFilterSubstationAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredFilterSubstationOptions = [...this.substationOptions];
      return;
    }

    this.filteredFilterSubstationOptions = this.substationOptions.filter((substation) => substation.name.toLowerCase().includes(normalizedQuery));
  }

  private filterProvinceAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredProvinceOptions = [...this.provinceOptions];
      return;
    }

    this.filteredProvinceOptions = this.provinceOptions.filter((province) => province.name.toLowerCase().includes(normalizedQuery));
  }

  private filterFilterProvinceAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredFilterProvinceOptions = [...this.filterProvinceOptions];
      return;
    }

    this.filteredFilterProvinceOptions = this.filterProvinceOptions.filter((province) => province.name.toLowerCase().includes(normalizedQuery));
  }

  private filterDistrictAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredDistrictOptions = [...this.districtOptions];
      return;
    }

    this.filteredDistrictOptions = this.districtOptions.filter((district) => district.name.toLowerCase().includes(normalizedQuery));
  }

  private filterFilterDistrictAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredFilterDistrictOptions = [...this.filterDistrictOptions];
      return;
    }

    this.filteredFilterDistrictOptions = this.filterDistrictOptions.filter((district) => district.name.toLowerCase().includes(normalizedQuery));
  }

  private filterLocalLevelAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredLocalLevelOptions = [...this.localLevelSearchOptions];
      return;
    }

    this.filteredLocalLevelOptions = this.localLevelSearchOptions.filter((localLevel) => localLevel.name?.toLowerCase().includes(normalizedQuery));
  }

  private filterFilterLocalLevelAutocomplete(query: string): void {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.filteredFilterLocalLevelOptions = [...this.filterLocalLevelOptions];
      return;
    }

    this.filteredFilterLocalLevelOptions = this.filterLocalLevelOptions.filter((localLevel) => localLevel.name?.toLowerCase().includes(normalizedQuery));
  }

  private findEntityByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.entityOptions.find((entity) => entity.name.trim().toLowerCase() === normalizedName);
  }

  private findSubstationByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.substationOptions.find((substation) => substation.name.trim().toLowerCase() === normalizedName);
  }

  private findProvinceByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.provinceOptions.find((province) => province.name.trim().toLowerCase() === normalizedName);
  }

  private findFilterProvinceByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.filterProvinceOptions.find((province) => province.name.trim().toLowerCase() === normalizedName);
  }

  private findDistrictByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.districtOptions.find((district) => district.name.trim().toLowerCase() === normalizedName);
  }

  private findFilterDistrictByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.filterDistrictOptions.find((district) => district.name.trim().toLowerCase() === normalizedName);
  }

  private findLocalLevelByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.localLevelSearchOptions.find((localLevel) => (localLevel.name ?? '').trim().toLowerCase() === normalizedName);
  }

  private findFilterLocalLevelByName(name: string): ManageGeneratorLookupOption | undefined {
    const normalizedName = name.trim().toLowerCase();
    if (!normalizedName) {
      return undefined;
    }

    return this.filterLocalLevelOptions.find((localLevel) => (localLevel.name ?? '').trim().toLowerCase() === normalizedName);
  }

  private extractAutocompleteValue(event: ManageGeneratorAutocompleteEvent): string {
    if ('query' in event && typeof event.query === 'string') {
      return event.query;
    }

    if ('value' in event && typeof event.value === 'string') {
      return event.value;
    }

    const input = (event as Event).target as HTMLInputElement | null;
    return input?.value ?? '';
  }

  private uploadGeneratorFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpSvc.requestCall(AuthEndPoints.UPLOAD_GENERATOR_FILE, ApiMethod.POST, formData, false, { suppressToast: true });
  }

  private extractFilePath(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const value = (response as { value?: unknown }).value;
    if (!value || typeof value !== 'object') {
      return null;
    }

    const data = (value as Record<string, unknown>)['data'];
    if (data && typeof data === 'object') {
      const filePath = (data as Record<string, unknown>)['file_path'];
      if (typeof filePath === 'string') {
        return filePath;
      }
    }

    const filePath = (value as Record<string, unknown>)['file_path'];
    return typeof filePath === 'string' ? filePath : null;
  }

  private extractGeneratorId(response: unknown): number | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const value = (response as { value?: unknown }).value;
    if (!value || typeof value !== 'object') {
      return null;
    }

    const generatorId = (value as Record<string, unknown>)['id'];
    const parsedId = Number(generatorId);
    return Number.isFinite(parsedId) ? parsedId : null;
  }

  private buildGeneratorPayload(filePath?: string | null): ManageGeneratorPayload {
    const formValue: ManageGeneratorFormValue = this.generatorForm.getRawValue();
    const localLevelName = this.getLocalLevelName(formValue.local_level_id);
    const contactRows = formValue.contact_details ?? [];
    const primaryContact = contactRows[0] ?? {
      id: null,
      person_name: '',
      email: '',
      mobile_no: '',
      designation: '',
    };
    const additionalContacts = contactRows.slice(1).map((contact) => ({
      id: contact.id ?? null,
      person_name: contact.person_name,
      email: contact.email,
      mobile_no: contact.mobile_no,
      designation: contact.designation || null,
    }));
    const payload: ManageGeneratorPayload = {
      generator_type: formValue.generator_type || null,
      fuel_type: formValue.fuel_type || null,
      generator_name: formValue.generator_name,
      promoter_company: this.getEntityName(formValue.entity_id) !== '-' ? this.getEntityName(formValue.entity_id) : formValue.promoter_company || null,
      project_unique_code: this.normalizeTextValue(formValue.project_unique_code),
      owner: formValue.owner || null,
      delivery_point: formValue.delivery_point || null,
      corridor: formValue.corridor || null,
      ppa_date: formValue.ppa_date || null,
      substation_id: formValue.substation_id,
      scada_station_code: this.resolveScadaStationCode(formValue),
      entity_id: formValue.entity_id,
      connecting_voltage_level_id: formValue.connecting_voltage_level_id,
      voltage_level_id: formValue.voltage_level_id,
      province_id: formValue.province_id,
      district_id: formValue.district_id,
      local_level_id: formValue.local_level_id,
      local_level: localLevelName !== '-' ? localLevelName : formValue.local_level || null,
      installed_capacity: this.toNumberOrNull(formValue.installed_capacity),
      capacity: this.toNumberOrNull(formValue.capacity),
      no_of_units: this.toIntegerOrNull(formValue.no_of_units),
      other_parameter: this.joinCommaSeparatedValues(formValue.other_parameter),
      log_sheet: formValue.log_sheet || null,
      logsheet: String(formValue.log_sheet ?? '').trim().toUpperCase() !== 'NO',
      act_as_substation: Boolean(formValue.act_as_substation),
      latitude: formValue.latitude || null,
      longitude: formValue.longitude || null,
      status: formValue.status,
      contact_id: primaryContact.id ?? null,
      person_name: primaryContact.person_name,
      email: primaryContact.email,
      mobile_no: primaryContact.mobile_no,
      designation: primaryContact.designation || null,
      contact_details: additionalContacts,
    };

    if (this.filePathCleared) {
      payload.file_path = null;
    }

    if (filePath) {
      payload.file_path = filePath;
    }

    if (this.modalMode === 'edit' && this.selectedGenerator) {
      payload.id = this.selectedGenerator.id;
    }

    return payload;
  }

  private buildQueryString(params: ManageGeneratorQueryParams): string {
    return buildQueryString(params, [GENERATOR_ALL_FILTER_VALUE]);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    downloadBlob(blob, fileName);
  }

  private buildGeneratorFileName(prefix: string): string {
    return buildTimestampFileName(prefix);
  }

  private extractPagination(value: unknown): ManageGeneratorPagination | null {
    return extractPagination<ManageGeneratorPagination>(value);
  }

  private clearLocalLevels(): void {
    this.localLevelOptions = [];
    this.localLevelSearchOptions = [];
    this.filteredLocalLevelOptions = [];
    this.generatorForm.patchValue(
      {
        local_level_id: null,
        local_level: '',
      },
      { emitEvent: false },
    );
    this.localLevelSearchTerm = '';
  }

  private getFilePreviewUrl(): string | null {
    if (this.selectedFilePreviewUrl) {
      return this.selectedFilePreviewUrl;
    }

    if (this.existingFilePath) {
      if (/^https?:\/\//i.test(this.existingFilePath)) {
        return this.existingFilePath;
      }

      const normalizedPath = this.existingFilePath.startsWith('/') ? this.existingFilePath : `/${this.existingFilePath}`;
      return `${environment.apiBaseURL}${normalizedPath}`;
    }

    return null;
  }

  private getApiErrorMessage(error: unknown): string | null {
    return getApiErrorMessage(error);
  }

  private toFormString(value: ManageGeneratorFormFieldValue): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }

  private resolveScadaStationCode(formValue: ManageGeneratorFormValue): string | null {
    return this.normalizeTextValue(formValue.scada_station_code);
  }

  private normalizeTextValue(value: string | null | undefined): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim();
    return normalizedValue || null;
  }

  private toLogSheetOptionValue(logsheet: boolean | null | undefined, legacyLogSheet: string | null | undefined): string {
    const normalizedLegacyLogSheet = this.normalizeTextValue(legacyLogSheet)?.toUpperCase();
    if (normalizedLegacyLogSheet === 'YES' || normalizedLegacyLogSheet === 'NO') {
      return normalizedLegacyLogSheet;
    }

    if (typeof logsheet === 'boolean') {
      return logsheet ? 'YES' : 'NO';
    }

    return 'YES';
  }

  private toNumberOrNull(value: ManageGeneratorFormFieldValue): number | null {
    const normalized = value === null || value === undefined ? '' : String(value).trim();
    if (!normalized) {
      return null;
    }

    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private toIntegerOrNull(value: ManageGeneratorFormFieldValue): number | null {
    const normalized = value === null || value === undefined ? '' : String(value).trim();
    if (!normalized) {
      return null;
    }

    const numeric = Number.parseInt(normalized, 10);
    return Number.isFinite(numeric) ? numeric : null;
  }

}
